import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  beiUmsatzAenderung, getUmsatzBis, getUmsatzToken, loescheUmsatzFreigabe, setUmsatzFreigabe,
} from '@/lib/umsatz';

/* Umsatz-Sperre (30.10.2026). Der Server liefert Umsatzzahlen nur dem Inhaber mit Freigabe-Token (backend/umsatz_sperre.py).
   Hier: Dialog „Mit Passwort anzeigen“, Hinweis „Umsätze sichtbar bis …“ und die Bausteine für gesperrte Stellen.
   Nach dem Entsperren oder Sperren wird der Seiteninhalt neu aufgebaut (key), damit er die Daten neu lädt. */

const Ctx = createContext({ inhaber: false, frei: false, anfordern: () => {}, sperren: () => {} });
export const useUmsatz = () => useContext(Ctx);

export function UmsatzProvider({ children }) {
  const { user } = useAuth();
  const inhaber = user?.role === 'admin';
  const [version, setVersion] = useState(0);
  const [offen, setOffen] = useState(false);
  const [pw, setPw] = useState('');
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [bis, setBis] = useState(getUmsatzBis());

  useEffect(() => beiUmsatzAenderung(() => { setBis(getUmsatzBis()); setVersion((v) => v + 1); }), []);

  // Nach Ablauf (30 Minuten) wird wieder verborgen
  useEffect(() => {
    if (!bis) return undefined;
    const rest = bis - Date.now();
    if (rest <= 0) { loescheUmsatzFreigabe(); return undefined; }
    const t = setTimeout(() => loescheUmsatzFreigabe(), rest + 200);
    return () => clearTimeout(t);
  }, [bis]);

  const sperren = useCallback(async () => {
    try { await api.post('/umsatz/sperren'); } catch { /* lokal sperren genügt */ }
    loescheUmsatzFreigabe();
  }, []);

  const entsperren = async (e) => {
    e?.preventDefault();
    if (!pw || laeuft) return;
    setLaeuft(true);
    setFehler('');
    try {
      const r = await api.post('/umsatz/entsperren', { passwort: pw });
      setUmsatzFreigabe(r.data.token, Date.now() + (r.data.gueltig_minuten || 30) * 60000);
      setPw('');
      setOffen(false);
      toast.success('Umsätze für 30 Minuten sichtbar');
    } catch (err) {
      setFehler(err.response?.data?.detail || 'Das hat nicht geklappt.');
    } finally {
      setLaeuft(false);
    }
  };

  const wert = useMemo(() => ({
    inhaber, frei: inhaber && !!getUmsatzToken(), anfordern: () => setOffen(true), sperren,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [inhaber, version, sperren]);

  return (
    <Ctx.Provider value={wert}>
      <div key={version}>{children}</div>
      {inhaber && bis && (
        <div className="fixed bottom-20 md:bottom-4 left-4 z-40 flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs shadow-md" data-testid="umsatz-frei-chip">
          <LockOpen className="h-3.5 w-3.5 text-marke" />
          <span>Umsätze sichtbar bis {new Date(bis).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
          <button type="button" onClick={sperren} className="font-semibold text-marke hover:underline" data-testid="umsatz-sperren">Sperren</button>
        </div>
      )}
      {offen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="umsatz-dialog">
          <form onSubmit={entsperren} className="w-full max-w-sm rounded-xl border border-border bg-card p-5 shadow-xl space-y-3">
            <div className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4" /> Umsätze anzeigen</div>
            <p className="text-sm text-muted-foreground">Gib dein CRM-Passwort noch einmal ein. Die Umsätze sind danach 30 Minuten sichtbar.</p>
            <input
              type="password" autoFocus autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Passwort" data-testid="umsatz-passwort"
            />
            {fehler && <p className="text-sm text-destructive" data-testid="umsatz-fehler">{fehler}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setOffen(false); setPw(''); setFehler(''); }} className="rounded-md px-3 py-2 text-sm hover:bg-muted">Abbrechen</button>
              <button type="submit" disabled={!pw || laeuft} className="rounded-md bg-marke px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="umsatz-ok">
                {laeuft ? 'Prüfe …' : 'Anzeigen'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Ctx.Provider>
  );
}

/* Eine Zahl: der Server liefert null, wenn sie verborgen ist. Dann steht „verborgen“ mit Schloss da — beim Inhaber
   zusätzlich „Mit Passwort anzeigen“. `format` formt die Zahl, wenn sie da ist. */
export function UmsatzWert({ wert, format, kurz = false, className = '' }) {
  const { inhaber, anfordern } = useUmsatz();
  if (wert !== null && wert !== undefined) return <span className={className}>{format ? format(wert) : String(wert)}</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-muted-foreground font-normal ${className}`} data-testid="umsatz-verborgen">
      <Lock className="h-3 w-3" aria-hidden="true" />
      <span>verborgen</span>
      {inhaber && !kurz && (
        <button type="button" onClick={anfordern} className="ml-1 text-xs font-semibold text-marke hover:underline" data-testid="umsatz-anzeigen">
          Mit Passwort anzeigen
        </button>
      )}
    </span>
  );
}

/* Ein ganzer Block (Diagramm, Tabelle): `gesperrt` kommt vom Server (umsatz_gesperrt). Nicht-Inhaber sehen einen
   schlichten Hinweis, der Inhaber den Knopf. */
export function UmsatzBlock({ gesperrt, children, titel = 'Umsätze' }) {
  const { inhaber, anfordern } = useUmsatz();
  if (!gesperrt) return children;
  return (
    <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="umsatz-block-verborgen">
      <Lock className="h-4 w-4" aria-hidden="true" />
      <span>{titel} sind verborgen.</span>
      {inhaber && <button type="button" onClick={anfordern} className="text-xs font-semibold text-marke hover:underline">Mit Passwort anzeigen</button>}
    </div>
  );
}
